// Trip presets and the composer's prompt compiler.
//
// A preset is a one-tap starting point for the trip composer: destination,
// suggested vibes and duration. For destinations with a verified dataset,
// the preset also references server-side starter picks, BY ID ONLY: the
// client sends presetId with the first concierge request and the server
// (api/_preset-picks.js) validates it and injects the picks into the
// system prompt. The picks never travel through chat text, because chat
// text is user-controlled (spoofable), user-visible (machine noise in
// their own bubble), and re-sent every turn (token waste). picksCount
// here is display metadata for the badge, not data the model sees.

export interface TripPreset {
  id: string;
  destination: string;
  tagline: string;
  vibes: string[];
  duration?: DurationId;
  // Number of server-side verified starter picks (badge display only).
  // Must match api/_preset-picks.js; the server is the source of truth.
  picksCount?: number;
}

export type DurationId = "weekend" | "4-5 days" | "a week" | "two weeks";
export type PartyId = "solo" | "couple" | "family" | "friends";
export type BudgetId = "shoestring" | "moderate" | "comfortable";

export const DURATIONS: { id: DurationId; label: string }[] = [
  { id: "weekend", label: "Weekend" },
  { id: "4-5 days", label: "4-5 days" },
  { id: "a week", label: "A week" },
  { id: "two weeks", label: "Two weeks" },
];

export const PARTIES: { id: PartyId; label: string }[] = [
  { id: "solo", label: "Solo" },
  { id: "couple", label: "Couple" },
  { id: "family", label: "Family" },
  { id: "friends", label: "Friends" },
];

export const BUDGETS: { id: BudgetId; label: string }[] = [
  { id: "shoestring", label: "$" },
  { id: "moderate", label: "$$" },
  { id: "comfortable", label: "$$$" },
];

// The composer's "Into" popover, grouped exactly as the design lays it out.
// Labels are verbatim from Landing - Field Notes.dc.html; they replaced an
// older flat list whose wording differed ("Temples & culture" was the old
// spelling of "Temples & history").
export const MOOD_GROUPS: { label: string; moods: string[] }[] = [
  { label: "food & drink", moods: ["Street food", "Coffee shops", "Markets", "Fine dining"] },
  { label: "culture", moods: ["Art & design", "Temples & history", "Live music", "Local craft"] },
  { label: "outside", moods: ["Beaches", "Hikes & nature", "Parks & rivers"] },
  { label: "pace & night", moods: ["Rooftop bars", "Nightlife", "Slow pace", "First-timer classics"] },
];

// Flat view of the same labels, for anything that just needs the vocabulary.
export const VIBES = MOOD_GROUPS.flatMap((g) => g.moods);

// Card images come from getCoverImage(destination) in trip-helpers.ts,
// the app's single source of destination photography.
export const PRESETS: TripPreset[] = [
  {
    id: "bangkok",
    destination: "Bangkok",
    tagline: "Street food · temples · rooftops",
    vibes: ["Street food", "Temples & history", "Rooftop bars"],
    duration: "4-5 days",
    picksCount: 18,
  },
  {
    id: "kyoto",
    destination: "Kyoto",
    tagline: "Temples · gardens · kissaten",
    vibes: ["Temples & history", "Coffee shops", "Slow pace"],
    duration: "4-5 days",
  },
  {
    id: "bali",
    destination: "Bali",
    tagline: "Beaches · rice terraces",
    vibes: ["Beaches", "Hikes & nature", "Slow pace"],
    duration: "a week",
  },
  {
    id: "barcelona",
    destination: "Barcelona",
    tagline: "Architecture · tapas · beach",
    vibes: ["Art & design", "Fine dining", "Beaches"],
    duration: "4-5 days",
  },
];

// The ONE place that decides whether a preset's server-side picks apply:
// the preset must have picks and the typed destination must still be the
// preset's destination (the user may have retyped it after tapping a
// card). The composer badge and the presetId sent to the server both go
// through this, so the UI can never promise picks the server won't inject.
export function presetPicksApply(
  preset: TripPreset | null | undefined,
  destination: string
): boolean {
  if (!preset?.picksCount) return false;
  return destination.trim().toLowerCase() === preset.destination.toLowerCase();
}

export interface ComposerInput {
  destination: string;
  // ISO dates (YYYY-MM-DD) from the composer's date picker. When a
  // complete range is set it wins over the coarse duration pill.
  startDate?: string | null;
  endDate?: string | null;
  duration?: DurationId | null;
  party?: PartyId | null;
  budget?: BudgetId | null;
  vibes: string[];
  notes?: string;
}

const BUDGET_PHRASES: Record<BudgetId, string> = {
  shoestring: "a tight budget",
  moderate: "a moderate budget",
  comfortable: "a comfortable budget, happy to spend on the good stuff",
};

const PARTY_PHRASES: Record<PartyId, string> = {
  solo: "traveling solo",
  couple: "traveling as a couple",
  family: "traveling as a family with kids",
  friends: "traveling with a group of friends",
};

// Compiles the composer state into the first concierge message, in plain
// natural language. Returns "" when the composer is completely empty, so
// the caller can fall back to the free path (navigate to an empty
// planner, no API call), matching the old empty-textarea behavior.
export function compileComposerPrompt(input: ComposerInput): string {
  const dest = input.destination.trim();
  const notes = input.notes?.trim();
  const empty =
    !dest &&
    !input.startDate &&
    !input.duration &&
    !input.party &&
    !input.budget &&
    input.vibes.length === 0 &&
    !notes;
  if (empty) return "";

  const parts: string[] = [];
  parts.push(dest ? `Plan a trip to ${dest}.` : "Help me plan a trip.");
  if (input.startDate && input.endDate) {
    parts.push(`Dates: ${input.startDate} to ${input.endDate}.`);
  } else if (input.startDate) {
    parts.push(`Starting ${input.startDate}.`);
    if (input.duration) parts.push(`Duration: ${input.duration}.`);
  } else if (input.duration) {
    parts.push(`Duration: ${input.duration}.`);
  }
  if (input.party) parts.push(`I'm ${PARTY_PHRASES[input.party]}.`);
  if (input.budget) parts.push(`I'm on ${BUDGET_PHRASES[input.budget]}.`);
  if (input.vibes.length) {
    parts.push(`I'm into: ${input.vibes.join(", ").toLowerCase()}.`);
  }
  if (notes) parts.push(notes);
  return parts.join(" ");
}
